import React, { useEffect, useState, useCallback } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Fleet from "@/pages/Fleet";
import Suppliers from "@/pages/Suppliers";
import Customers from "@/pages/Customers";
import Production from "@/pages/Production";
import ProductionMaterials from "@/pages/ProductionMaterials";
import ProductionPackages from "@/pages/ProductionPackages";
import ProductionWaste from "@/pages/ProductionWaste";
import ProductionChecker from "@/pages/ProductionChecker";
import ProductionReports from "@/pages/ProductionReports";
import Inbound from "@/pages/Inbound";
import Outbound from "@/pages/Outbound";
import SalesInvoices from "@/pages/SalesInvoices";
import Receivables from "@/pages/Receivables";
import SalesAnalytics from "@/pages/SalesAnalytics";
import Returns from "@/pages/Returns";
import Opname from "@/pages/Opname";
import OpeningBalance from "@/pages/OpeningBalance";
import Reconciliation from "@/pages/Reconciliation";
import Checker from "@/pages/Checker";
import SystemHealth from "@/pages/SystemHealth";
import Reports from "@/pages/Reports";
import AuditTrail from "@/pages/AuditTrail";
import Settings from "@/pages/Settings";

// AuthContext
export const AuthContext = React.createContext(null);

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sid });
        window.history.replaceState({}, document.title, "/");
        navigate("/", { replace: true, state: { user: res.data } });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="text-slate-500 font-mono text-sm">Mengautentikasi...</div>
    </div>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

function Protected({ children }) {
  const { user, loading } = React.useContext(AuthContext);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 font-mono text-sm">Memuat...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="checker" element={<Checker />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="system-health" element={<SystemHealth />} />
        <Route path="products" element={<Products />} />
        <Route path="fleet" element={<Fleet />} />
        <Route path="customers" element={<Customers />} />
        <Route path="production" element={<Production />} />
        <Route path="production/materials" element={<ProductionMaterials />} />
        <Route path="production/packages" element={<ProductionPackages />} />
        <Route path="production/waste" element={<ProductionWaste />} />
        <Route path="production/checker" element={<ProductionChecker />} />
        <Route path="production/reports" element={<ProductionReports />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="inbound" element={<Inbound />} />
        <Route path="outbound" element={<Outbound />} />
        <Route path="sales" element={<SalesInvoices />} />
        <Route path="receivables" element={<Receivables />} />
        <Route path="analytics" element={<SalesAnalytics />} />
        <Route path="returns" element={<Returns />} />
        <Route path="opname" element={<Opname />} />
        <Route path="opening-balance" element={<OpeningBalance />} />
        <Route path="reports" element={<Reports />} />
        <Route path="audit" element={<AuditTrail />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
